import { SectionCard } from './ui/SectionCard';
import { useMemo } from 'react';
import { Link } from '@tanstack/react-router';
import type { PositionWithTv } from '@/lib/api';
import { fmtMoney, fmtPercent } from '@/lib/format';

type Props = {
  positions: PositionWithTv[];
  totalValue: number;
};

export function ConcentrationCard({ positions, totalValue }: Props) {
  const top = useMemo(
    () =>
      [...positions]
        .sort((a, b) => b.walletImpact.currentValue - a.walletImpact.currentValue)
        .slice(0, 10),
    [positions],
  );

  const max = top[0]?.walletImpact.currentValue ?? 1;

  return (
    <SectionCard className="h-full">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-medium text-zinc-200">Top concentration</h3>
        <span className="text-[11px] text-zinc-400">Top 10 by value</span>
      </div>
      <ul className="space-y-1.5">
        {top.map((p) => {
          const pct = totalValue > 0 ? (p.walletImpact.currentValue / totalValue) * 100 : 0;
          const widthPct = (p.walletImpact.currentValue / max) * 100;
          const pnlPct =
            (p.walletImpact.unrealizedProfitLoss / Math.max(0.01, p.walletImpact.totalCost)) * 100;
          return (
            <li key={p.instrument.ticker}>
              <Link
                to="/positions/$ticker"
                params={{ ticker: p.instrument.ticker }}
                className="block group"
              >
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="font-mono text-[11px] text-zinc-400 w-12 shrink-0">
                    {p.instrument.ticker.split('_')[0]}
                  </span>
                  <span className="text-xs text-zinc-300 truncate flex-1 min-w-0 group-hover:text-zinc-100 transition-colors">
                    {p.instrument.name}
                  </span>
                  <span className="text-[11px] text-zinc-400 num">
                    {fmtPercent(pct)}
                  </span>
                  <span
                    className={
                      pnlPct >= 0
                        ? 'text-[11px] text-emerald-400 num w-14 text-right'
                        : 'text-[11px] text-rose-400 num w-14 text-right'
                    }
                  >
                    {pnlPct >= 0 ? '+' : ''}
                    {pnlPct.toFixed(1)}%
                  </span>
                </div>
                <div className="h-1 rounded-full bg-zinc-900 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-indigo-500/80 to-cyan-400/60"
                    style={{ width: `${widthPct}%` }}
                  />
                </div>
                <div className="text-[10px] text-zinc-400 mt-0.5">
                  {fmtMoney(p.walletImpact.currentValue)}
                </div>
              </Link>
            </li>
          );
        })}
      </ul>
    </SectionCard>
  );
}
